// src/pages/lkp/LookupStep1Review.tsx
//
// Step 1 — DISCOVERY, master/detail. Left: the discovered fields (per section) as a
// scannable list with status + entity name. Right: the selected proposal's evidence,
// an editable LOOKUP ENTITY name (so two fields point to one entity), and Accept/Reject.
// Bulk "Accept all" per section. Mismatched value sets across one entity are flagged.
// No code-values are finalized here — that's Step 3.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  lookup_getCandidates, lookup_saveStep1Review,
  type LookupCandidatesBlock, type Step1Decision,
} from "../api/lkpAPI";

interface Props { entityId: string; onAccepted?: () => void; }
type Fam = "lookup_field" | "code_description_pair" | "dependent_lookup";
const ref = (r?: { table: string; field: string }) => (r ? `${r.table}.${r.field}` : "—");
const norm = (v: any) => String(v ?? "").trim().toLowerCase();
const cid = (it: any) => it.candidate_id;
const fidOf = (it: any) => it.family === "lookup_field" ? ref(it.field) : it.family === "code_description_pair" ? ref(it.code) : ref(it.determinant);

export default function Step1Review({ entityId, onAccepted }: Props) {
  const [block, setBlock] = useState<LookupCandidatesBlock | null>(null);
  const [fam, setFam] = useState<Fam>("lookup_field");
  const [sel, setSel] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState<"all" | "review" | "accepted" | "rejected">("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ m: string; e?: boolean } | null>(null);
  const note = (m: string, e = false) => { setFlash({ m, e }); setTimeout(() => setFlash(null), 3500); };

  const load = useCallback(async () => {
    try { const r = await lookup_getCandidates(entityId); setBlock(r.data); setNames({}); }
    catch { setBlock(null); }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const items = (block?.items || []) as any[];
  const nameOf = (it: any) => (names[cid(it)] ?? it.entity_name ?? "");

  const groups = useMemo(() => {
    const g: Record<string, { fields: string[]; sets: Record<string, Set<string>> }> = {};
    items.forEach((it) => {
      if (it.family !== "lookup_field" && it.family !== "code_description_pair") return;
      if (it.status === "REJECTED") return;
      const key = norm(nameOf(it)); if (!key) return;
      const fid = fidOf(it);
      const vals = it.family === "lookup_field" ? (it.observed_values || []) : Object.keys(it.code_to_description || {});
      (g[key] = g[key] || { fields: [], sets: {} }).fields.push(fid);
      g[key].sets[fid] = new Set(vals.map(norm));
    });
    return g;
  }, [items, names]);
  const divergence = (key: string) => {
    const grp = groups[key]; if (!grp || grp.fields.length < 2) return false;
    const sets = Object.values(grp.sets).filter((s) => s.size);
    for (let i = 0; i < sets.length; i++) for (let j = i + 1; j < sets.length; j++) {
      const a = sets[i], b = sets[j]; const inter = [...a].filter((x) => b.has(x)).length;
      if (inter / (new Set([...a, ...b]).size || 1) < 0.4) return true;
    } return false;
  };
  const coMembers = (it: any) => (groups[norm(nameOf(it))]?.fields || []).filter((f) => f !== fidOf(it));

  const inSection = items.filter((i) => i.family === fam);
  const inBucket = (i: any) => bucket === "all" ? true
    : bucket === "accepted" ? i.status === "ACCEPTED"
    : bucket === "rejected" ? i.status === "REJECTED"
    : (i.status !== "ACCEPTED" && i.status !== "REJECTED");
  const listed = inSection.filter((i) => {
    if (!inBucket(i)) return false;
    if (q) { const s = q.toLowerCase(); return fidOf(i).toLowerCase().includes(s) || nameOf(i).toLowerCase().includes(s); }
    return true;
  });
  const openCount = (f: Fam) => items.filter((i) => i.family === f && i.status !== "ACCEPTED" && i.status !== "REJECTED").length;
  const bktCount = (b: "all" | "review" | "accepted" | "rejected") => inSection.filter((i) =>
    b === "all" ? true : b === "accepted" ? i.status === "ACCEPTED" : b === "rejected" ? i.status === "REJECTED" : (i.status !== "ACCEPTED" && i.status !== "REJECTED")).length;
  const selected = items.find((i) => cid(i) === sel && i.family === fam) || listed[0] || null;
  useEffect(() => { if (listed.length && !listed.find((i) => cid(i) === sel)) setSel(cid(listed[0])); }, [fam, bucket, listed.length]); // eslint-disable-line

  const save = async (decisions: Step1Decision[], tag: string) => {
    setBusy(tag);
    try { const r = await lookup_saveStep1Review(entityId, decisions); setBlock(r.candidates); note(`Saved ${decisions.length}.`); onAccepted?.(); }
    catch (e: any) { note(e?.detail || "Save failed", true); } finally { setBusy(null); }
  };
  const accept = (it: any) => save([{ candidate_id: cid(it), decision: "ACCEPT", entity_name: nameOf(it) || undefined }], cid(it));
  const reject = (it: any) => save([{ candidate_id: cid(it), decision: "REJECT" }], cid(it));
  const reopen = (it: any) => save([{ candidate_id: cid(it), decision: "RESET" }], cid(it));
  const saveName = (it: any) => { if (nameOf(it) !== (it.entity_name ?? "")) save([{ candidate_id: cid(it), decision: "UPDATE", entity_name: nameOf(it) }], cid(it)); };
  const acceptAll = () => {
    const open = inSection.filter((i) => i.status !== "ACCEPTED" && i.status !== "REJECTED");
    if (!open.length) { note("Nothing open to accept in this section."); return; }
    save(open.map((it) => ({ candidate_id: cid(it), decision: "ACCEPT" as const, entity_name: nameOf(it) || undefined })), "all");
  };

  const dot = (s?: string) => s === "ACCEPTED" ? "ok" : s === "REJECTED" ? "no" : "pr";
  const TABS: Array<{ k: Fam; label: string }> = [
    { k: "lookup_field", label: "Lookup fields" },
    { k: "code_description_pair", label: "Code ↔ display" },
    { k: "dependent_lookup", label: "Dependent lookups" },
  ];

  return (
    <div className="s1">
      <style>{CSS}</style>
      <div className="s1-head">
        <div><div className="s1-title">Discover &amp; name lookup entities</div>
          <div className="s1-sub">Pick a field on the left, name the entity it belongs to, Accept or Reject. Values are evidence only — finalized in Step 3.</div></div>
        <div className="s1-htools">
          <div className="s1-buckets">
            {(["all", "review", "accepted", "rejected"] as const).map((b) => (
              <button key={b} className={"s1-bk " + (bucket === b ? "on " : "") + b} onClick={() => setBucket(b)}>
                {b === "all" ? "All" : b === "review" ? "Needs review" : b === "accepted" ? "Accepted" : "Rejected"}<span className="s1-bkn">{bktCount(b)}</span>
              </button>
            ))}
          </div>
          {(bucket === "review" || bucket === "all") && <button className="s1-btn ok" disabled={!!busy} onClick={acceptAll}>{busy === "all" ? "…" : `Accept all (${openCount(fam)})`}</button>}
        </div>
      </div>

      {flash && <div className={"s1-flash " + (flash.e ? "err" : "ok")}>{flash.m}</div>}

      <div className="s1-tabs">
        {TABS.map((t) => (
          <button key={t.k} className={"s1-tab " + (fam === t.k ? "on" : "")} onClick={() => { setFam(t.k); setSel(null); }}>
            {t.label}<span className="s1-cnt">{openCount(t.k)}</span>
          </button>
        ))}
      </div>

      <div className="s1-body">
        {/* master list */}
        <div className="s1-list">
          <input className="s1-search" placeholder="Search field / entity…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="s1-items">
            {listed.map((it) => {
              const key = norm(nameOf(it));
              return (
                <button key={cid(it)} className={"s1-li " + (selected && cid(selected) === cid(it) ? "on" : "")} onClick={() => setSel(cid(it))}>
                  <span className={"s1-dot " + dot(it.status)} />
                  <span className="s1-li-f">{fidOf(it)}</span>
                  {fam !== "dependent_lookup" && <span className="s1-li-e">{nameOf(it) || "— unnamed —"}</span>}
                  {fam !== "dependent_lookup" && divergence(key) && <span className="s1-li-warn" title="value sets differ across fields on this entity">⚠</span>}
                </button>
              );
            })}
            {listed.length === 0 && <div className="s1-empty small">Nothing here. Run detect or toggle “show reviewed”.</div>}
          </div>
        </div>

        {/* detail */}
        <div className="s1-detail">
          {!selected ? <div className="s1-empty big">Select a field.</div> : (() => {
            const it = selected; const key = norm(nameOf(it)); const co = coMembers(it); const div = divergence(key);
            const busyRow = busy === cid(it);
            return (
              <>
                <div className="s1-drow">
                  {fam === "lookup_field" && <span className="s1-fid">{ref(it.field)}</span>}
                  {fam === "code_description_pair" && <><span className="s1-fid">{ref(it.code)}</span><span className="s1-ar">→</span><span className="s1-fid">{ref(it.description)}</span></>}
                  {fam === "dependent_lookup" && <><span className="s1-fid">{ref(it.determinant)}</span><span className="s1-ar">governs</span><span className="s1-fid">{ref(it.dependent)}</span></>}
                  <div className="s1-flex" />
                  {it.confidence != null && <span className="s1-conf">conf {it.confidence}</span>}
                  <span className={"s1-st " + dot(it.status)}>{(it.status || "PROPOSED").toLowerCase()}</span>
                </div>

                {fam !== "dependent_lookup" && (
                  <div className="s1-ent">
                    <label>Lookup entity</label>
                    <input className="s1-entin" value={nameOf(it)} placeholder="e.g. Country Lookup"
                      onChange={(e) => setNames((p) => ({ ...p, [cid(it)]: e.target.value }))} onBlur={() => saveName(it)} />
                    {co.length > 0 && <span className="s1-co">also on this entity: {co.join(", ")}</span>}
                    {div && <span className="s1-diverge">⚠ value sets differ across fields — consider splitting</span>}
                  </div>
                )}

                <div className="s1-ev">
                  {fam === "lookup_field" && <span>{it.observed_distinct ?? (it.observed_values || []).length} distinct · from {it.values_source || "profile"}</span>}
                  {fam === "code_description_pair" && it.pair_stats && <span>{it.pair_stats.rows ?? 0} rows · {it.pair_stats.code_count ?? 0} codes · {it.pair_stats.functional ? "clean 1:1" : `${it.pair_stats.conflicting ?? 0} conflicting`}</span>}
                  {fam === "dependent_lookup" && it.dependency_stats && <span>{it.dependency_stats.determinant_values ?? 0} determinant values · {it.dependency_stats.functional ? "functional" : "one-to-many"}{it.proven_on_rows ? " · proven on rows" : ""}</span>}
                  {it.evidence?.rationale && <div className="s1-rat">“{it.evidence.rationale}”</div>}
                </div>

                <div className="s1-valsbox">
                  <div className="s1-valsh">Sample values <span>(evidence — not finalized)</span></div>
                  <div className="s1-vals">
                    {fam === "lookup_field" && (it.observed_values || []).slice(0, 60).map((v: string) => <span key={v} className="s1-v">{v}</span>)}
                    {fam === "code_description_pair" && Object.entries(it.code_to_description || {}).slice(0, 40).map(([c, d]: any) => <span key={c} className="s1-v"><b>{c}</b>{d ? ` ${d}` : ""}</span>)}
                    {fam === "dependent_lookup" && Object.entries(it.value_map || {}).slice(0, 12).map(([d, deps]: any) => <span key={d} className="s1-v"><b>{d}</b> → {(deps || []).join(", ")}</span>)}
                  </div>
                </div>

                <div className="s1-acts">
                  {it.status !== "ACCEPTED" && <button className="s1-btn ok" disabled={busyRow} onClick={() => accept(it)}>Accept as lookup</button>}
                  {it.status !== "REJECTED" && <button className="s1-btn no" disabled={busyRow} onClick={() => reject(it)}>Reject</button>}
                  {(it.status === "ACCEPTED" || it.status === "REJECTED") && <button className="s1-btn" disabled={busyRow} onClick={() => reopen(it)}>Reopen</button>}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.s1{--ink:#14202b;--surface:#fff;--surface2:#f7f9f7;--line:#dde3df;--line2:#e9ede9;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--wait:#bd7a15;--warn:#c23c28;--warn-bg:#f7e0da;--infer:#4a51c0;--infer-bg:#e6e7f7;
  font-family:'Inter',system-ui,sans-serif;color:var(--tx);font-size:14px}
.s1 *{box-sizing:border-box}.s1-fid,.s1-v,.s1-conf,.s1-entin,.s1-li-f{font-family:'IBM Plex Mono',ui-monospace,monospace}
.s1-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:12px}
.s1-title{font-weight:600;font-size:17px;font-family:'Space Grotesk',Inter,sans-serif}
.s1-sub{font-size:12.5px;color:var(--tx2);margin-top:3px;max-width:70ch}
.s1-htools{display:flex;align-items:center;gap:12px}
.s1-buckets{display:inline-flex;align-items:center;background:var(--surface2);border:1px solid var(--line);border-radius:9px;padding:3px;gap:2px}
.s1-bk{border:none;background:transparent;padding:6px 12px;font-size:12.5px;line-height:1;cursor:pointer;color:var(--tx2);font-family:inherit;border-radius:7px;display:inline-flex;align-items:center;gap:7px;font-weight:500;transition:background .12s,color .12s}
.s1-bk:hover{color:var(--tx)}
.s1-bk.on{background:var(--surface);box-shadow:0 1px 2px rgba(16,32,43,.10);font-weight:600}
.s1-bk.on.all{color:var(--tx)}
.s1-bk.on.review{color:var(--infer)} .s1-bk.on.accepted{color:var(--gov)} .s1-bk.on.rejected{color:var(--warn)}
.s1-bkn{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11px;min-width:16px;text-align:center;background:#00000010;color:var(--tx2);border-radius:20px;padding:1px 7px;font-weight:600}
.s1-bk.on.all .s1-bkn{background:var(--surface2);color:var(--tx2)}
.s1-bk.on.review .s1-bkn{background:var(--infer-bg);color:var(--infer)}
.s1-bk.on.accepted .s1-bkn{background:var(--gov-bg);color:var(--gov)}
.s1-bk.on.rejected .s1-bkn{background:var(--warn-bg);color:var(--warn)}
.s1-flash{padding:9px 13px;border-radius:8px;font-size:13px;margin-bottom:12px;background:var(--gov-bg);color:var(--gov)}
.s1-flash.err{background:var(--warn-bg);color:var(--warn)}
.s1-tabs{display:flex;gap:16px;border-bottom:1px solid var(--line);margin-bottom:14px}
.s1-tab{border:none;background:transparent;padding:9px 2px;font-size:13.5px;color:var(--tx2);cursor:pointer;font-weight:500;border-bottom:2px solid transparent;display:inline-flex;gap:8px;align-items:center;font-family:inherit}
.s1-tab.on{color:var(--ink);border-bottom-color:var(--gov)}
.s1-cnt{font-family:'IBM Plex Mono';font-size:11.5px;background:var(--surface2);border:1px solid var(--line);border-radius:20px;padding:1px 8px;color:var(--tx2)}
.s1-tab.on .s1-cnt{background:var(--gov-bg);color:var(--gov);border-color:transparent}
.s1-body{display:grid;grid-template-columns:300px 1fr;gap:14px;min-height:440px}
.s1-list{border:1px solid var(--line);border-radius:12px;background:var(--surface);display:flex;flex-direction:column;overflow:hidden}
.s1-search{border:none;border-bottom:1px solid var(--line);padding:10px 12px;font-size:13px;outline:none;font-family:inherit}
.s1-items{overflow-y:auto;flex:1;max-height:520px}
.s1-li{display:flex;align-items:center;gap:8px;width:100%;text-align:left;border:none;border-bottom:1px solid var(--line2);background:transparent;padding:9px 11px;cursor:pointer;font-family:inherit}
.s1-li:hover{background:var(--surface2)}.s1-li.on{background:var(--infer-bg)}
.s1-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.s1-dot.ok{background:var(--gov)}.s1-dot.no{background:var(--warn)}.s1-dot.pr{background:var(--infer)}
.s1-li-f{font-size:12px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:140px}
.s1-li-e{font-size:11px;color:var(--tx3);margin-left:auto;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:110px}
.s1-li-warn{color:var(--warn)}
.s1-detail{border:1px solid var(--line);border-radius:12px;background:var(--surface);padding:16px 18px;overflow-y:auto}
.s1-drow{display:flex;align-items:center;gap:9px;margin-bottom:12px}
.s1-fid{font-size:14px;font-weight:600}.s1-ar{color:var(--tx3)}.s1-flex{flex:1}
.s1-conf{font-size:11px;color:var(--tx3)}
.s1-st{font-size:10.5px;font-weight:700;text-transform:uppercase;padding:2px 8px;border-radius:5px}
.s1-st.ok{background:var(--gov-bg);color:var(--gov)}.s1-st.no{background:var(--warn-bg);color:var(--warn)}.s1-st.pr{background:var(--infer-bg);color:var(--infer)}
.s1-ent{display:flex;align-items:center;gap:9px;margin-bottom:13px;flex-wrap:wrap}
.s1-ent label{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--tx2);font-weight:600}
.s1-entin{border:1px solid var(--line);border-radius:7px;padding:7px 11px;font-size:13px;min-width:260px;outline:none;font-weight:600}
.s1-entin:focus{border-color:var(--gov)}
.s1-co{font-size:11.5px;color:var(--tx2);background:var(--surface2);border:1px solid var(--line2);border-radius:6px;padding:3px 8px}
.s1-diverge{font-size:11.5px;color:var(--warn);background:var(--warn-bg);border-radius:6px;padding:3px 8px;font-weight:600}
.s1-ev{font-size:12px;color:var(--tx2);margin-bottom:12px}
.s1-rat{font-style:italic;margin-top:5px}
.s1-valsbox{border:1px solid var(--line2);border-radius:9px;padding:11px;margin-bottom:14px;background:var(--surface2)}
.s1-valsh{font-size:11px;letter-spacing:.05em;text-transform:uppercase;color:var(--tx2);font-weight:600;margin-bottom:8px}
.s1-valsh span{text-transform:none;letter-spacing:0;font-weight:400;color:var(--tx3)}
.s1-vals{display:flex;flex-wrap:wrap;gap:6px;max-height:220px;overflow-y:auto}
.s1-v{font-size:11.5px;background:var(--surface);border:1px solid var(--line2);border-radius:6px;padding:3px 8px}
.s1-v b{color:var(--gov)}
.s1-acts{display:flex;gap:8px;flex-wrap:wrap}
.s1-btn{border:1px solid var(--line);background:var(--surface);border-radius:8px;padding:8px 14px;font-size:12.5px;font-weight:500;cursor:pointer;font-family:inherit;color:var(--tx)}
.s1-btn:hover{border-color:var(--tx2);background:var(--surface2)}
.s1-btn.ok{background:var(--gov);border-color:var(--gov);color:#fff}.s1-btn.ok:hover{background:#0b7560}
.s1-btn.no{color:var(--warn)}.s1-btn.no:hover{border-color:var(--warn);background:var(--warn-bg)}
.s1-btn:disabled{opacity:.5;cursor:default}
.s1-empty{color:var(--tx3)}.s1-empty.small{padding:16px;font-size:12.5px}.s1-empty.big{display:grid;place-items:center;height:100%;font-size:13px}
@media(max-width:820px){.s1-body{grid-template-columns:1fr}}
`;
